// Esquemas zod e conversões dos formulários de tenancy (Parte 4.6): validação local espelha a do servidor
// (CNPJ/CPF/CEP por formato; a validação completa — dígitos verificadores, UF ↔ município IBGE — é do servidor).
import { z } from 'zod';

import type {
  AddressInput,
  CreateSiteInput,
  CreateTenantInput,
  InitialAdminInput,
  LegalEntity,
  LegalEntityInput,
  Site,
  SiteLocationInput,
  UpdateSiteInput,
} from '../api/queries';

export const BRAZILIAN_STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

/** Fusos brasileiros — primeiros na lista (mais usados hoje) para não regredir a ordenação do seletor atual. */
const BRAZILIAN_TIME_ZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Rio_Branco',
  'America/Noronha',
] as const;

/**
 * Todos os fusos IANA (ADR-024/REQ-TEN-029: um site fora do Brasil não deve ficar preso à lista brasileira).
 * `Intl.supportedValuesOf` é suportado por todos os navegadores-alvo (Baseline 2023); sem fallback estático —
 * o backend já valida qualquer fuso IANA (`SiteLocationRules.ResolveTimeZone`, TZDB via NodaTime).
 */
export const TIME_ZONES: readonly string[] = (() => {
  try {
    const all = Intl.supportedValuesOf('timeZone');
    const brazilian = BRAZILIAN_TIME_ZONES.filter((zone) => all.includes(zone));
    const rest = all.filter(
      (zone) => !BRAZILIAN_TIME_ZONES.includes(zone as (typeof BRAZILIAN_TIME_ZONES)[number]),
    );
    return [...brazilian, ...rest];
  } catch {
    return BRAZILIAN_TIME_ZONES;
  }
})();

export const requiredString = (message: string) => z.string().trim().min(1, message);
const required = requiredString;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const addressSchema = z.object({
  street: required('forms.errors.required'),
  number: required('forms.errors.required'),
  complement: z.string().trim(),
  district: required('forms.errors.required'),
  city: required('forms.errors.required'),
  state: z.enum(BRAZILIAN_STATES, 'forms.errors.state'),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}-?\d{3}$/, 'forms.errors.postalCode'),
});
export type AddressValues = z.infer<typeof addressSchema>;

const TAX_REGIME_VALUES = ['LucroReal', 'LucroPresumido', 'SimplesNacional', 'Other'] as const;

export const legalEntitySchema = z.object({
  legalName: required('forms.errors.required'),
  tradeName: z.string().trim(),
  cnpj: z
    .string()
    .trim()
    .regex(/^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/, 'forms.errors.cnpj'),
  stateRegistration: z.string().trim(),
  municipalRegistration: z.string().trim(),
  taxRegime: z.enum(TAX_REGIME_VALUES),
  representativeName: required('forms.errors.required'),
  representativeCpf: z
    .string()
    .trim()
    .regex(/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, 'forms.errors.cpf'),
  representativeEmail: z.email('forms.errors.email'),
  institutionalEmail: z.email('forms.errors.email'),
  institutionalPhone: z.string().trim(),
  address: addressSchema,
});
export type LegalEntityValues = z.infer<typeof legalEntitySchema>;

export const siteSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9-]{2,12}$/, 'forms.errors.siteCode'),
  name: required('forms.errors.required'),
  timeZoneId: required('forms.errors.required'),
  phone: z.string().trim(),
  status: z.enum(['Planned', 'Active']),
  address: addressSchema,
  ibgeMunicipalityCode: z
    .string()
    .trim()
    .regex(/^(\d{7})?$/, 'forms.errors.ibge'),
  latitude: z.string().trim(),
  longitude: z.string().trim(),
});
export type SiteValues = z.infer<typeof siteSchema>;

export const EMPTY_ADDRESS: AddressValues = {
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: 'PR',
  postalCode: '',
};

export const EMPTY_LEGAL_ENTITY: LegalEntityValues = {
  legalName: '',
  tradeName: '',
  cnpj: '',
  stateRegistration: '',
  municipalRegistration: '',
  taxRegime: 'LucroReal',
  representativeName: '',
  representativeCpf: '',
  representativeEmail: '',
  institutionalEmail: '',
  institutionalPhone: '',
  address: EMPTY_ADDRESS,
};

export const EMPTY_SITE: SiteValues = {
  code: '',
  name: '',
  timeZoneId: 'America/Sao_Paulo',
  phone: '',
  status: 'Active',
  address: EMPTY_ADDRESS,
  ibgeMunicipalityCode: '',
  latitude: '',
  longitude: '',
};

function orNull(value: string): string | null {
  return value.trim() ? value.trim() : null;
}

function numberOrNull(value: string): number | null {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

export function toAddressInput(values: AddressValues): AddressInput {
  return {
    street: values.street,
    number: values.number,
    complement: orNull(values.complement),
    district: values.district,
    city: values.city,
    state: values.state,
    postalCode: values.postalCode.includes('-')
      ? values.postalCode
      : `${values.postalCode.slice(0, 5)}-${values.postalCode.slice(5)}`,
    country: 'BR',
  };
}

export function toLegalEntityInput(values: LegalEntityValues): LegalEntityInput {
  return {
    legalName: values.legalName,
    tradeName: orNull(values.tradeName),
    registrationNumber: values.cnpj,
    stateRegistration: orNull(values.stateRegistration),
    municipalRegistration: orNull(values.municipalRegistration),
    taxRegime: values.taxRegime,
    legalRepresentative: {
      name: values.representativeName,
      cpf: orNull(values.representativeCpf),
      email: values.representativeEmail,
    },
    institutionalEmail: values.institutionalEmail,
    institutionalPhone: orNull(values.institutionalPhone),
    address: toAddressInput(values.address),
  };
}

/** Localização estruturada (REQ-TEN-025): coordenadas manuais viram `LocationSource=Manual`; sem elas, `Address`. */
export function toLocationInput(values: SiteValues): SiteLocationInput {
  const latitude = numberOrNull(values.latitude);
  const longitude = numberOrNull(values.longitude);
  const hasCoordinates = latitude !== null && longitude !== null;
  return {
    state: values.address.state,
    ibgeMunicipalityCode: orNull(values.ibgeMunicipalityCode),
    // O assistente só cria tenants brasileiros por enquanto (ADR-024/GIY-068: regionFreeText fica para quando a UI
    // ganhar um seletor de país — o backend já aceita, mas nenhum formulário ainda envia country != BR).
    regionFreeText: null,
    latitude: hasCoordinates ? latitude : null,
    longitude: hasCoordinates ? longitude : null,
    locationSource: hasCoordinates ? 'Manual' : 'Address',
  };
}

export function toCreateSiteInput(values: SiteValues): CreateSiteInput {
  return {
    code: values.code.toUpperCase(),
    name: values.name,
    timeZoneId: values.timeZoneId,
    phone: orNull(values.phone),
    status: values.status,
    address: toAddressInput(values.address),
    location: toLocationInput(values),
  };
}

export function toUpdateSiteInput(values: SiteValues): UpdateSiteInput {
  return {
    name: values.name,
    timeZoneId: values.timeZoneId,
    phone: orNull(values.phone),
    status: values.status,
    address: toAddressInput(values.address),
    location: toLocationInput(values),
  };
}

export function siteToValues(site: Site): SiteValues {
  const state = BRAZILIAN_STATES.find((s) => s === site.address.state) ?? 'PR';
  return {
    code: site.code,
    name: site.name,
    timeZoneId: site.timeZoneId,
    phone: site.phone ?? '',
    status: site.status === 'Planned' ? 'Planned' : 'Active',
    address: {
      street: site.address.street,
      number: site.address.number,
      complement: site.address.complement ?? '',
      district: site.address.district,
      city: site.address.city,
      state,
      postalCode: site.address.postalCode,
    },
    ibgeMunicipalityCode: site.ibgeMunicipalityCode ?? '',
    latitude: site.latitude === null ? '' : String(site.latitude),
    longitude: site.longitude === null ? '' : String(site.longitude),
  };
}

/** Ordem dos dias no editor semanal (Parte 4.4: `weekly` com chaves em inglês minúsculo). */
export const WEEK_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];

export interface WeeklyRange {
  readonly start: string;
  readonly end: string;
}
export type WeeklyValues = Record<WeekDay, readonly WeeklyRange[]>;

export const DEFAULT_WEEKLY: WeeklyValues = {
  monday: [{ start: '07:00', end: '19:00' }],
  tuesday: [{ start: '07:00', end: '19:00' }],
  wednesday: [{ start: '07:00', end: '19:00' }],
  thursday: [{ start: '07:00', end: '19:00' }],
  friday: [{ start: '07:00', end: '19:00' }],
  saturday: [],
  sunday: [],
};

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

export type WeeklyErrorKey =
  'calendar.errors.invalidTime' | 'calendar.errors.startBeforeEnd' | 'calendar.errors.overlap';

/** Erros locais do editor semanal (espelho de `TEN_CALENDAR_INVALID_RANGES`): chave i18n por dia. */
export function validateWeekly(weekly: WeeklyValues): Partial<Record<WeekDay, WeeklyErrorKey>> {
  const errors: Partial<Record<WeekDay, WeeklyErrorKey>> = {};
  for (const day of WEEK_DAYS) {
    const ranges = weekly[day];
    for (const range of ranges) {
      if (!TIME.test(range.start) || !TIME.test(range.end)) {
        errors[day] = 'calendar.errors.invalidTime';
        break;
      }
      if (range.start >= range.end) {
        errors[day] = 'calendar.errors.startBeforeEnd';
        break;
      }
    }
    if (errors[day]) {
      continue;
    }
    const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
    for (let index = 1; index < sorted.length; index += 1) {
      const previous = sorted[index - 1];
      const current = sorted[index];
      if (previous && current && current.start < previous.end) {
        errors[day] = 'calendar.errors.overlap';
        break;
      }
    }
  }
  return errors;
}

export function weeklyFromResponse(
  weekly: Readonly<Record<string, readonly WeeklyRange[]>>,
): WeeklyValues {
  const result: Record<WeekDay, readonly WeeklyRange[]> = { ...DEFAULT_WEEKLY };
  for (const day of WEEK_DAYS) {
    result[day] = (weekly[day] ?? []).map((range) => ({ start: range.start, end: range.end }));
  }
  return result;
}

export function weeklyToRequest(weekly: WeeklyValues): Record<string, WeeklyRange[]> {
  const result: Record<string, WeeklyRange[]> = {};
  for (const day of WEEK_DAYS) {
    result[day] = weekly[day].map((range) => ({ start: range.start, end: range.end }));
  }
  return result;
}

/** Data civil de hoje (`yyyy-MM-dd`) no fuso dado — sem hora, como o servidor a interpreta. */
export function todayIso(timeZoneId: string | null, now: Date = new Date()): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZoneId ?? 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(now);
}

export function addDaysIso(dateIso: string, days: number): string {
  const [year, month, day] = dateIso.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
  return date.toISOString().slice(0, 10);
}

// ---- Assistente de criação de tenant (Parte 4.8) e bootstrap do administrador (REQ-TEN-016) ----

/** Passo 1: identificação do tenant (código curto imutável, 3–8 alfanuméricos) + pessoa jurídica. */
export const tenantIdentitySchema = legalEntitySchema.extend({
  code: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{3,8}$/, 'forms.errors.code'),
  displayName: required('forms.errors.required'),
  defaultLocale: z.enum(['pt-BR', 'en-US']),
});
export type TenantIdentityValues = z.infer<typeof tenantIdentitySchema>;

export const EMPTY_TENANT_IDENTITY: TenantIdentityValues = {
  ...EMPTY_LEGAL_ENTITY,
  code: '',
  displayName: '',
  defaultLocale: 'pt-BR',
};

/**
 * Os cinco tipos de vínculo do PPL (Parte 6.2; decisão do PO, 2026-10-02). O bootstrap só cria o vínculo que a Matriz indica para a
 * função do administrador (Sócio e CLT); PJ (caso a caso) e os demais respondem `423` — a rede escolhe outro tipo ou nenhum e registra o
 * vínculo depois pela ficha da pessoa.
 */
export const LINK_TYPES = ['Socio', 'CLT', 'PJ', 'CooperadoCPESQ', 'Estagiario'] as const;
export type LinkType = (typeof LINK_TYPES)[number];

export const initialAdminSchema = z.object({
  givenName: required('forms.errors.required'),
  familyName: required('forms.errors.required'),
  email: z.email('forms.errors.email'),
  preferredLocale: z.enum(['pt-BR', 'en-US']),
  linkType: z.enum(LINK_TYPES),
  linkEffectiveFrom: z.string().trim().regex(ISO_DATE, 'forms.errors.date'),
});
export type InitialAdminValues = z.infer<typeof initialAdminSchema>;

export function emptyInitialAdmin(today: string): InitialAdminValues {
  return {
    givenName: '',
    familyName: '',
    email: '',
    preferredLocale: 'pt-BR',
    linkType: 'CLT',
    linkEffectiveFrom: today,
  };
}

const admissionShape = {
  admissionPath: z.enum(['AdmissaoPlena', 'ModoDeExcecao']),
  conformancePlanDocumentId: z.string().trim(),
  conformancePlanDeadline: z.string().trim(),
};

/** `ModoDeExcecao` exige o documento do plano de conformidade e a data-limite (REQ-TEN-011). */
function refineAdmission(
  values: {
    admissionPath: string;
    conformancePlanDocumentId: string;
    conformancePlanDeadline: string;
  },
  ctx: z.RefinementCtx,
): void {
  if (values.admissionPath !== 'ModoDeExcecao') {
    return;
  }
  if (!UUID.test(values.conformancePlanDocumentId)) {
    ctx.addIssue({
      code: 'custom',
      path: ['conformancePlanDocumentId'],
      message: 'forms.errors.uuid',
    });
  }
  if (!ISO_DATE.test(values.conformancePlanDeadline)) {
    ctx.addIssue({
      code: 'custom',
      path: ['conformancePlanDeadline'],
      message: 'forms.errors.date',
    });
  }
}

/** Via de admissão (REQ-TEN-011). */
export const admissionSchema = z.object(admissionShape).superRefine(refineAdmission);
export type AdmissionValues = z.infer<typeof admissionSchema>;

/** Dados cadastrais editáveis pela rede (`PUT /network/tenants/{id}`; o código é imutável). */
export const tenantUpdateSchema = z
  .object({
    ...admissionShape,
    displayName: required('forms.errors.required'),
    defaultLocale: z.enum(['pt-BR', 'en-US']),
  })
  .superRefine(refineAdmission);
export type TenantUpdateValues = z.infer<typeof tenantUpdateSchema>;

/** Na edição da PJ o CPF do responsável começa em branco (a API só devolve a máscara) e tem de ser digitado de novo. */
export function legalEntityToValues(legalEntity: LegalEntity): LegalEntityValues {
  const regime = TAX_REGIME_VALUES.find((value) => value === legalEntity.taxRegime) ?? 'Other';
  const state = BRAZILIAN_STATES.find((s) => s === legalEntity.address.state) ?? 'PR';
  return {
    legalName: legalEntity.legalName,
    tradeName: legalEntity.tradeName ?? '',
    cnpj: legalEntity.registrationNumberFormatted,
    stateRegistration: legalEntity.stateRegistration ?? '',
    municipalRegistration: legalEntity.municipalRegistration ?? '',
    taxRegime: regime,
    representativeName: legalEntity.legalRepresentative.name,
    representativeCpf: '',
    representativeEmail: legalEntity.legalRepresentative.email,
    institutionalEmail: legalEntity.institutionalEmail,
    institutionalPhone: legalEntity.institutionalPhone ?? '',
    address: {
      street: legalEntity.address.street,
      number: legalEntity.address.number,
      complement: legalEntity.address.complement ?? '',
      district: legalEntity.address.district,
      city: legalEntity.address.city,
      state,
      postalCode: legalEntity.address.postalCode,
    },
  };
}

export const EMPTY_ADMISSION: AdmissionValues = {
  admissionPath: 'AdmissaoPlena',
  conformancePlanDocumentId: '',
  conformancePlanDeadline: '',
};

export function toInitialAdminInput(values: InitialAdminValues): InitialAdminInput {
  return {
    personId: null,
    person: {
      givenName: values.givenName,
      familyName: values.familyName,
      email: values.email,
      preferredLocale: values.preferredLocale,
    },
    link: { linkType: values.linkType, effectiveFrom: values.linkEffectiveFrom },
  };
}

export interface TenantWizardState {
  readonly identity: TenantIdentityValues;
  readonly sites: readonly SiteValues[];
  readonly admin: InitialAdminValues | null;
  readonly admission: AdmissionValues;
}

export function toCreateTenantInput(state: TenantWizardState): CreateTenantInput {
  const { code, displayName, defaultLocale, ...legalEntity } = state.identity;
  const exception = state.admission.admissionPath === 'ModoDeExcecao';
  return {
    code: code.toUpperCase(),
    displayName,
    defaultLocale,
    legalEntity: toLegalEntityInput(legalEntity),
    sites: state.sites.map(toCreateSiteInput),
    initialAdmin: state.admin ? toInitialAdminInput(state.admin) : null,
    admissionPath: state.admission.admissionPath,
    conformancePlanDocumentId: exception ? state.admission.conformancePlanDocumentId : null,
    ...(exception ? { conformancePlanDeadline: state.admission.conformancePlanDeadline } : {}),
  };
}
